import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import PageLayout from './PageLayout';
import Popup from './Popup';
import { programmTexts } from '../locales/texts';
import config from '../config';
import theme, { mobileBreakpoint } from '../theme';
import { StarBadgeWithIcon } from './shared/CommonElements';
import { useAuth } from '../AuthContext';

const Section = styled.section`
  background: ${({ bg }) => bg || 'white'};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-left: 10px solid ${({ borderColor }) => borderColor || props.theme.colors.accent2};
  border-right: 2px solid ${({ borderColor }) => borderColor || props.theme.colors.accent2};
  border-top: 2px solid ${({ borderColor }) => borderColor || props.theme.colors.accent2};
  border-bottom: 2px solid ${({ borderColor }) => borderColor || props.theme.colors.accent2};
  margin: 40px auto;
  max-width: 800px;
  padding: 48px 32px;
  @media (max-width: ${mobileBreakpoint}px) {
    margin: 24px 10px;
    padding: 28px 18px;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 16px;
  text-align: center;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 1.8rem;
  }
`;

const SectionText = styled.p`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 24px;
  text-align: center;
`;

const PriceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0 auto;
  margin-bottom: 8px;
  th, td {
    border: 1px solid ${props => props.theme.colors.accent2};
    padding: 10px 8px;
    text-align: center;
    font-size: 1rem;
  }
  th {
    background: ${props => props.theme.colors.accent1};
    color: ${props => props.theme.colors.primary};
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin: 40px auto 0 auto;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
`;

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 10;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 10;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;
const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  color: ${props => props.theme.colors.primary};
`;

const Programm = () => {
  const theme = useTheme();
  const { token } = useAuth();
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const viewportWidth = window.innerWidth;
    fetch(config.getApiUrl(`/api/prices?viewportWidth=${viewportWidth}`), { headers })
      .then(res => {
        if (!res.ok) throw new Error('Fehler beim Laden der Preise');
        return res.json();
      })
      .then(data => {
        setPrices(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  // Scroll to anchor after prices are loaded
  useEffect(() => {
    if (!loading && window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <PageLayout onTerminClick={handleOpenPopup}>
      <HeroSection>
        <HeroTitle>
          <StarBadgeWithIcon />
          <MainTitle>{programmTexts.heroTitle}</MainTitle>
        </HeroTitle>
        <Subtitle>{programmTexts.heroSubtitle}</Subtitle>
        <Description>{programmTexts.heroDescription}</Description>
      </HeroSection>
      {error && <IntroText style={{ color: theme.colors.secondary }}>{error}</IntroText>}
      {loading && <IntroText>Lade Preise ...</IntroText>}
      {!loading && prices && <>
        <Section
          id="nachhilfe"
          bg="rgba(188, 231, 253, 0.13)"
          borderColor={theme.colors.accent2}
        >
          <SectionTitle>{programmTexts.nachhilfe.title}</SectionTitle>
          <SectionText>{programmTexts.nachhilfe.text}</SectionText>
          <PriceTable>
            <thead>
              <tr>
                {prices.nachhilfe.headers.map((header, idx) => (
                  <th key={idx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prices.nachhilfe.rows.map((row, idx) => (
                <tr key={idx}>
                  {row.map((cell, cidx) => (
                    <td key={cidx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </PriceTable>
        </Section>
        <Section
          id="sprachunterricht"
          bg="rgba(196, 146, 177, 0.08)"
          borderColor={theme.colors.accent3}
        >
          <SectionTitle>{programmTexts.sprachunterricht.title}</SectionTitle>
          <SectionText>{programmTexts.sprachunterricht.text}</SectionText>
          <PriceTable>
            <thead>
              <tr>
                {prices.sprachunterricht.headers.map((header, idx) => (
                  <th key={idx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prices.sprachunterricht.rows.map((row, idx) => (
                <tr key={idx}>
                  {row.map((cell, cidx) => (
                    <td key={cidx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </PriceTable>
        </Section>
        <Section
          id="gruppenkurse"
          bg="rgba(219, 39, 98, 0.08)"
          borderColor={theme.colors.secondary}
        >
          <SectionTitle>{programmTexts.gruppenkurse.title}</SectionTitle>
          <SectionText>{programmTexts.gruppenkurse.text}</SectionText>
          <PriceTable>
            <thead>
              <tr>
                {prices.gruppenkurse.headers.map((header, idx) => (
                  <th key={idx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prices.gruppenkurse.rows.map((row, idx) => (
                <tr key={idx}>
                  {row.map((cell, cidx) => (
                    <td key={cidx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </PriceTable>
        </Section>
      </>}
      <Popup 
        isOpen={isPopupOpen} 
        onClose={handleClosePopup}
        title="Probestunde"
      />
    </PageLayout>
  );
};

export default Programm; 